import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/common/PageHeader";
import StrumentiTabs from "@/components/preventivi/StrumentiTabs";
import EmptyStateSection from "@/components/common/EmptyStateSection";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { eur, quotaSuBase } from "@/lib/formato";
import { leggiImporto, messaggioErrore, rigaCodice } from "@/lib/preventivi";
import { useAuth } from "@/contexts/AuthContext";
import {
  Upload,
  FileWarning,
  Info,
  Calculator,
  FilePlus,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const TIPI_FORNITURA = [
  { valore: "cucina", label: "Cucina" },
  { valore: "divano", label: "Divano" },
  { valore: "camera", label: "Camera" },
  { valore: "armadio", label: "Armadio" },
  { valore: "cabina", label: "Cabina armadio" },
  { valore: "altro", label: "Altro" },
];

/* ------------------------------------------------------------------ */
/* Utility formattazione                                              */
/* ------------------------------------------------------------------ */
/* Gli importi passano tutti da `eur` in @/lib/formato: una porta sola per
   tutta l'app, con il punto delle migliaia anche sotto le cinque cifre. */

/* Il parser degli importi sta in @/lib/preventivi (`leggiImporto`): lo stesso
   che usa il dialogo della configurazione nel preventivatore. */
const parseItNumber = leggiImporto;

/* ------------------------------------------------------------------ */
export default function CalcolatorePage() {
  const { profile } = useAuth() || {};
  const titolare = profile?.ruolo === "titolare";
  // La vista interna (catena, percentuali, nomi dei livelli) esiste solo per il
  // titolare, e parte SPENTA a ogni apertura: si accende con un gesto esplicito
  // e non si ricorda. Davanti al cliente lo schermo parte sempre pulito.
  const [vistaInterna, setVistaInterna] = useState(false);
  const interna = titolare && vistaInterna;

  const [params, setParams] = useState(null);
  const [listinoStr, setListinoStr] = useState("");
  const [promoStr, setPromoStr] = useState("");
  const [composizioneStr, setComposizioneStr] = useState("");
  const [chosen, setChosen] = useState(null); // apre sul più alto dopo il fetch
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfWarning, setPdfWarning] = useState(null);
  const [apriCrea, setApriCrea] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get("/calcolatore/parametri")
      .then((r) => {
        setParams(r.data);
        const piuAlto = (r.data.ricarichi || [])
          .slice()
          .sort((a, b) => b.ordinamento - a.ordinamento)[0];
        setChosen(piuAlto?.livello || null); // apri sempre sul più alto
      })
      .catch((e) => {
        const msg =
          e.response?.status === 403
            ? "Il tuo ruolo non è abilitato al calcolatore."
            : messaggioErrore(e);
        toast.error(msg);
      });
  }, []);

  const listino = useMemo(() => parseItNumber(listinoStr), [listinoStr]);
  const promo = useMemo(() => parseItNumber(promoStr), [promoStr]);
  const composizione = useMemo(() => parseItNumber(composizioneStr), [composizioneStr]);

  // I livelli che chi guarda può usare, dal più alto al suo pavimento.
  const livelli = useMemo(() => {
    const tutti = (params?.ricarichi || []).slice().sort((a, b) => b.ordinamento - a.ordinamento);
    const pavimento =
      tutti.find((x) => x.livello === params?.livello_min_mio_ruolo)?.ordinamento ?? Infinity;
    return tutti.filter((r) => r.ordinamento >= pavimento);
  }, [params]);

  const canCalc =
    listino != null && promo != null && chosen != null && params?.ricarichi?.length;

  /* Ogni tasto premuto fa partire un calcolo, e le risposte non tornano per
     forza nell'ordine in cui sono partite: una risposta vecchia (per esempio
     senza il totale composizione) poteva arrivare per ultima e coprire quella
     giusta. Vale solo la risposta dell'ultima richiesta. */
  const ultimaRichiesta = useRef(0);
  const compute = async () => {
    if (!canCalc) return;
    const questa = ++ultimaRichiesta.current;
    setBusy(true);
    try {
      const r = await api.post("/calcolatore/calcola", {
        totale_listino: listino,
        totale_promotion: promo,
        livello: chosen,
        totale_composizione: composizione ?? undefined,
        origine: pdfWarning?.origine || "manual",
      });
      if (questa === ultimaRichiesta.current) setResult(r.data);
    } catch (e) {
      if (questa === ultimaRichiesta.current) toast.error(messaggioErrore(e));
    } finally {
      if (questa === ultimaRichiesta.current) setBusy(false);
    }
  };

  // Ricomputa quando i due totali sono validi e cambio livello o input.
  useEffect(() => {
    if (canCalc) compute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listino, promo, composizione, chosen]);

  const onPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPdfBusy(true);
    setPdfWarning(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/calcolatore/parse-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.ok) {
        setListinoStr(String(r.data.totale_listino).replace(".", ","));
        setPromoStr(String(r.data.totale_promotion).replace(".", ","));
        setPdfWarning({
          kind: "unverified",
          origine: "pdf",
          message:
            "Ho letto questi numeri dal PDF. Controllali sul foglio del fornitore prima di dire il prezzo al cliente.",
        });
        toast.success("PDF letto: controlla i numeri");
      } else {
        setPdfWarning({
          kind: "failed",
          message: r.data.warning || "Non riesco a leggere questo PDF: scrivi i totali qui sotto.",
        });
        toast.warning("Il PDF non si legge: scrivi i totali a mano");
      }
    } catch (err) {
      setPdfWarning({ kind: "failed", message: messaggioErrore(err) });
      toast.error("Il PDF non si è aperto: riprova o scrivi i totali a mano");
    } finally {
      setPdfBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const campiTotali = (
    <section
      className="rounded-xl border border-line bg-surface p-4 md:p-5"
      aria-labelledby="calc-totali-titolo"
      data-testid="calc-totali"
    >
      <h2 id="calc-totali-titolo" className="font-display text-h3 text-ink-strong">
        Totali del fornitore
      </h2>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={onPdf}
            className="hidden"
            data-testid="calc-pdf-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={pdfBusy}
            className="w-full justify-center"
            data-testid="calc-pdf-btn"
          >
            <Upload aria-hidden />
            {pdfBusy ? "Leggo il PDF…" : "Carica il PDF del fornitore"}
          </Button>
          <p className="text-small text-ink-2">
            Il gestionale prova a trovare i totali nel PDF. Controlla sempre le cifre sul foglio del fornitore.
            Fino a circa 5 MB.
          </p>
          {pdfWarning ? (
            <div
              role="status"
              className={cn(
                "flex items-start gap-2 rounded-md border p-3 text-small",
                pdfWarning.kind === "failed"
                  ? "border-danger-line bg-danger-bg text-danger-ink"
                  : "border-warning-line bg-warning-bg text-warning-ink",
              )}
              data-testid="calc-pdf-warning"
            >
              <FileWarning className="mt-0.5 size-4 shrink-0" aria-hidden />
              <p>{pdfWarning.message}</p>
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-3 text-small text-ink-2" aria-hidden="true">
          <span className="h-px flex-1 bg-line" />
          oppure scrivili qui
          <span className="h-px flex-1 bg-line" />
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
          <div className="space-y-inside">
            <Label htmlFor="cli-listino">Totale listino</Label>
            <Input
              id="cli-listino"
              inputMode="decimal"
              placeholder="10.100,68"
              value={listinoStr}
              onChange={(e) => setListinoStr(e.target.value)}
              data-testid="calc-input-listino"
            />
          </div>
          <div className="space-y-inside">
            <Label htmlFor="cli-promo">Totale promotion</Label>
            <Input
              id="cli-promo"
              inputMode="decimal"
              placeholder="4.356,79"
              value={promoStr}
              onChange={(e) => setPromoStr(e.target.value)}
              data-testid="calc-input-promo"
            />
          </div>
        </div>
        <div className="space-y-inside">
          <Label htmlFor="cli-comp">Totale composizione, IVA inclusa</Label>
          <Input
            id="cli-comp"
            inputMode="decimal"
            placeholder="21.165,74"
            value={composizioneStr}
            onChange={(e) => setComposizioneStr(e.target.value)}
            aria-describedby="cli-comp-aiuto"
            data-testid="calc-input-composizione"
          />
          <p id="cli-comp-aiuto" className="text-small text-ink-2">
            È il listino che vede il cliente: serve a mostrargli quanto risparmia.
          </p>
        </div>
      </div>
    </section>
  );

  const risultato = interna ? (
    <VistaInterna
      params={params}
      chosen={chosen}
      setChosen={setChosen}
      result={result}
      busy={busy}
      onCrea={() => setApriCrea(true)}
    />
  ) : result ? (
    <VistaTrattativa
      result={result}
      livelli={livelli}
      chosen={chosen}
      setChosen={setChosen}
      busy={busy}
      onCrea={() => setApriCrea(true)}
    />
  ) : (
    <EmptyStateSection
      testId="calc-empty"
      icon={Calculator}
      title={busy ? "Calcolo…" : "Scrivi i totali del fornitore per vedere il prezzo."}
      description={busy ? undefined : "Bastano il totale listino e il totale promotion."}
    />
  );

  return (
    <section data-testid="page-calcolatore" className="grid grid-cols-[minmax(0,1fr)] gap-card">
      <PageHeader
        testId="calc-header"
        title="Preventivi"
        description="Dai totali del fornitore al prezzo per il cliente. Si parte dal ricarico più alto e si scende trattando."
        actions={
          <>
            <StrumentiTabs />
            {interna && params?.livello_min_mio_ruolo ? (
              <Badge
                variant="outline"
                className="border-brand-subtle bg-brand-subtle text-brand-ink"
                data-testid="calc-role-floor-badge"
              >
                Il tuo minimo · {params.livello_min_mio_ruolo}
              </Badge>
            ) : null}
            {titolare ? (
              <div className="flex min-h-11 items-center gap-inside" data-testid="calc-vista-interna">
                <Switch
                  id="calc-vista-interna"
                  checked={vistaInterna}
                  onCheckedChange={setVistaInterna}
                  data-testid="calc-vista-interna-switch"
                />
                <Label htmlFor="calc-vista-interna" className="flex min-h-11 items-center text-ink-2">
                  Vista interna
                </Label>
              </div>
            ) : null}
          </>
        }
      />

      {interna ? (
        <>
          {campiTotali}
          {risultato}
        </>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-card lg:grid-cols-2">
          {campiTotali}
          {risultato}
        </div>
      )}

      <CreaPreventivoDialog
        open={apriCrea}
        onOpenChange={setApriCrea}
        mostraLivello={interna}
        calcolo={
          result
            ? {
                listino: result.input.totale_listino,
                promozione: result.input.totale_promotion,
                livello: result.vendita.livello_scelto,
                prezzo: result.vendita.prezzo_vendita,
              }
            : null
        }
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* La vista trattativa: quello che si può girare verso il cliente.     */
/* Prezzo, listino IVA inclusa, risparmio in euro. Nessuna percentuale,
   nessun costo, nessun nome di livello: si scende e si risale di un passo,
   e i passi non hanno nome. L'unica traccia del margine è la riga COD in
   basso a sinistra, che il cliente non sa leggere e il venditore sì.     */
/* ------------------------------------------------------------------ */
function VistaTrattativa({ result, livelli, chosen, setChosen, busy, onCrea }) {
  const v = result.vendita;
  const idx = livelli.findIndex((l) => l.livello === chosen);
  const listinoIvato = result.input.totale_composizione;
  const risparmio = v.sconto_cliente;
  const cod = rigaCodice(v);
  const vai = (i) => {
    if (i >= 0 && i < livelli.length && !busy) setChosen(livelli[i].livello);
  };

  return (
    <section
      className="rounded-xl border border-line bg-surface p-4 md:p-5"
      data-testid="calc-result"
      aria-busy={busy}
      aria-labelledby="calc-prezzo-titolo"
    >
      <h2 id="calc-prezzo-titolo" className="text-ink-2">
        Prezzo per il cliente, IVA inclusa
      </h2>
      <p
        className={cn("mt-1 font-figure text-hero tabular-nums text-ink-strong transition-opacity duration-150", busy && "opacity-60")}
        data-testid="calc-headline-price"
      >
        {eur(v.prezzo_vendita)}
      </p>
      {listinoIvato != null ? (
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1">
          <div className="contents" data-testid="calc-listino-ivato">
            <dt className="text-ink-2">Listino</dt>
            <dd className="text-right tabular-nums text-ink-2 line-through">{eur(listinoIvato)}</dd>
          </div>
          {risparmio != null && risparmio > 0 ? (
            <div className="contents" data-testid="calc-risparmio">
              <dt className="text-ink-2">Il cliente risparmia</dt>
              <dd className="text-right font-bold tabular-nums text-success-ink">{eur(risparmio)}</dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <p className="mt-3 text-small text-ink-2">
          Con il totale composizione IVA inclusa compaiono il listino e il risparmio.
        </p>
      )}

      {livelli.length > 1 ? (
        <div className="mt-5 border-t border-line-subtle pt-4" data-testid="calc-trattativa-passi">
          <p className="font-semibold text-ink" id="calc-passi-titolo">
            Trattativa
          </p>
          <div className="mt-3 flex flex-col gap-3" role="group" aria-labelledby="calc-passi-titolo">
            <div className="flex items-center justify-center">
              {livelli.map((l, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => vai(i)}
                  disabled={busy}
                  aria-label={`Passo ${i + 1} di ${livelli.length}${i === 0 ? ", il prezzo più alto" : ""}`}
                  aria-pressed={i === idx}
                  data-testid={`calc-passo-${i}`}
                  className="grid h-11 w-11 place-items-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-wait"
                >
                  <span className={i === idx ? "block h-2.5 w-6 rounded-full bg-brand" : "block h-2.5 w-2.5 rounded-full bg-line-input"} />
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => vai(idx + 1)}
                disabled={busy || idx < 0 || idx >= livelli.length - 1}
                aria-label="Scendi di un passo"
                data-testid="calc-scendi"
              >
                <ChevronDown aria-hidden />
                Scendi
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => vai(idx - 1)}
                disabled={busy || idx <= 0}
                aria-label="Risali di un passo"
                data-testid="calc-risali"
              >
                <ChevronUp aria-hidden />
                Risali
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex flex-col gap-3 border-t border-line-subtle pt-4">
        <Button onClick={onCrea} data-testid="calc-crea-preventivo" className="w-full justify-center">
          <FilePlus aria-hidden />
          Crea il preventivo da questo calcolo
        </Button>
        <p className="text-caption tabular-nums text-ink-3" data-testid="calc-cod">
          {cod}
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* La vista interna, solo del titolare: la catena intera, le           */
/* percentuali, i livelli per nome. È quella di prima del 24-09.       */
/* ------------------------------------------------------------------ */
function VistaInterna({ params, chosen, setChosen, result, busy, onCrea }) {
  return (
    <>
      {/* Level selector */}
      {params ? (
        <Card className="p-card">
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold text-foreground">Livello di trattativa</p>
          </div>
          <div
            role="tablist"
            className="grid grid-cols-5 gap-2"
            data-testid="calc-level-selector"
          >
            {params.ricarichi
              .slice()
              .sort((a, b) => b.ordinamento - a.ordinamento) // M5 → M1
              .map((r) => {
                const disabled =
                  r.ordinamento <
                  (params.ricarichi.find((x) => x.livello === params.livello_min_mio_ruolo)
                    ?.ordinamento ?? 999);
                const active = chosen === r.livello;
                return (
                  <button
                    key={r.livello}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-disabled={disabled}
                    disabled={disabled}
                    onClick={() => !disabled && setChosen(r.livello)}
                    data-testid={`calc-level-${r.livello}`}
                    data-testid-state={
                      disabled ? "disabled" : active ? "active" : "enabled"
                    }
                    className={[
                      "rounded-md border py-2 text-sm font-semibold transition-colors",
                      disabled
                        ? "bg-muted text-muted-foreground/60 cursor-not-allowed"
                        : active
                          ? "border-transparent bg-brand-subtle text-brand-subtle-foreground"
                          : "bg-card text-foreground hover:bg-accent",
                    ].join(" ")}
                  >
                    <div>{r.livello}</div>
                    <div
                      className={[
                        "text-xs font-normal mt-0.5",
                        disabled
                          ? "text-muted-foreground/60"
                          : "text-muted-foreground",
                      ].join(" ")}
                    >
                      {r.percentuale != null ? `+${Number(r.percentuale)}%` : " "}
                    </div>
                  </button>
                );
              })}
          </div>
          <p className="text-xs text-muted-foreground mt-3 flex items-start gap-inside">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Sotto il minimo del ruolo il livello è disabilitato, anche sul server.
          </p>
        </Card>
      ) : null}

      {/* Result */}
      {result ? (
        <Card className="p-6" data-testid="calc-result">
          {/* Senza `catena` non c'e' la colonna di sinistra. */}
          <div className={result.catena ? "grid gap-6 lg:grid-cols-3" : "grid gap-6"}>
            {result.catena ? (
            <div className="lg:col-span-2 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Catena di calcolo
              </p>
              <div className="rounded-md border divide-y divide-border">
                <ChainRow
                  label="Totale Listino"
                  value={eur(result.input.totale_listino)}
                  testid="row-listino"
                />
                <ChainRow
                  label={`Sconti listino (${result.catena.sconti_listino_percentuali
                    .map((s) => `-${s}%`)
                    .join(" · ")})`}
                  value={eur(result.catena.listino_dopo_sconti)}
                  testid="row-listino-scontato"
                  muted
                />
                <ChainRow
                  label="Totale Promotion"
                  value={eur(result.input.totale_promotion)}
                  testid="row-promo"
                />
                <ChainRow
                  label={`Sconto promozione (${result.catena.sconti_promotion_percentuali
                    .map((s) => `-${s}%`)
                    .join(" · ")})`}
                  value={eur(result.catena.promotion_dopo_sconti)}
                  testid="row-promo-scontato"
                  muted
                />
                <ChainRow
                  label="Imponibile costo"
                  value={eur(result.catena.imponibile_costo)}
                  testid="row-imponibile"
                />
                <ChainRow
                  label={`IVA +${result.catena.iva_percentuale}%`}
                  value={eur(result.catena.costo_totale)}
                  testid="row-costo"
                  bold
                />
                <ChainRow
                  label={`Ricarico ${result.vendita.livello_scelto} (+${result.vendita.ricarico_percentuale}%)`}
                  value={eur(result.vendita.prezzo_vendita)}
                  testid="row-prezzo"
                  emphasize
                />
              </div>
            </div>
            ) : null}

            <div className="space-y-4">
              <div
                className="rounded-md bg-primary p-4 text-primary-foreground"
                data-testid="calc-headline-price"
              >
                <p className="text-xs uppercase text-primary-foreground/80">
                  Prezzo {result.vendita.livello_scelto}
                </p>
                <p className="text-3xl font-semibold mt-1 tabular-nums">
                  {eur(result.vendita.prezzo_vendita)}
                </p>
                {result.vendita.sconto_cliente != null ? (
                  <p
                    className="text-xs text-primary-foreground mt-2"
                    data-testid="calc-sconto-cliente"
                  >
                    Sconto al cliente:{" "}
                    <span className="font-semibold tabular-nums">
                      {quotaSuBase(
                        result.vendita.sconto_cliente,
                        result.input.totale_composizione,
                      )}
                    </span>
                  </p>
                ) : null}
              </div>

              <div
                className="rounded-md border p-4"
                data-testid="calc-floor-price"
              >
                <p className="text-xs uppercase text-muted-foreground">
                  Il tuo minimo · {result.vendita.livello_min_ruolo}
                </p>
                <p className="text-xl font-semibold text-foreground mt-1 tabular-nums">
                  {eur(result.vendita.prezzo_al_min_ruolo)}
                </p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  È la riga da tenere d&#39;occhio: sotto questo prezzo non puoi scendere.
                </p>
              </div>

              <div className="rounded-md border p-4">
                <p className="text-xs uppercase text-muted-foreground mb-2">
                  Tutti i livelli
                </p>
                <div className="space-y-1">
                  {Object.entries(result.vendita.prezzi_per_livello)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([liv, r]) => (
                      <div
                        key={liv}
                        className="flex items-center justify-between text-xs"
                        data-testid={`calc-price-${liv}`}
                      >
                        <span
                          className={
                            r.disponibile_per_ruolo
                              ? "text-muted-foreground"
                              : "text-muted-foreground/60 line-through"
                          }
                        >
                          {liv}{r.percentuale != null ? ` · +${r.percentuale}%` : ""}
                        </span>
                        <span
                          className={[
                            "tabular-nums",
                            r.disponibile_per_ruolo
                              ? "font-medium text-foreground"
                              : "text-muted-foreground/60",
                          ].join(" ")}
                        >
                          {eur(r.prezzo_vendita)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-card border-t pt-group flex flex-col gap-group sm:flex-row sm:items-center">
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">
                Da qui nasce il preventivo
              </p>
              <p className="text-xs text-muted-foreground mt-micro leading-relaxed">
                Questi numeri entrano come prima configurazione, calcolati di nuovo dal
                server. Non si riscrive niente a mano.
              </p>
            </div>
            <Button
              onClick={onCrea}
              data-testid="calc-crea-preventivo"
              className="shrink-0"
            >
              <FilePlus className="h-4 w-4 mr-1.5" />
              Crea il preventivo da questo calcolo
            </Button>
          </div>
        </Card>
      ) : (
        <EmptyStateSection
          testId="calc-empty"
          icon={Calculator}
          title={
            busy
              ? "Calcolo…"
              : "Inserisci Totale Listino e Totale Promotion per vedere la catena."
          }
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Il ponte: dal calcolo al preventivo.                               */
/* Chiede il cliente ADESSO e non prima, perche' il calcolatore si apre
   anche solo per vedere dove cade un prezzo, senza un cliente in testa. */
/* ------------------------------------------------------------------ */
function CreaPreventivoDialog({ open, onOpenChange, calcolo, mostraLivello }) {
  const nav = useNavigate();
  const [clienti, setClienti] = useState(null);
  const [form, setForm] = useState({
    cliente_id: "",
    titolo: "",
    tipo_fornitura: "cucina",
    etichetta: "",
  });
  const [busy, setBusy] = useState(false);

  // I clienti si chiedono all'apertura, non al caricamento della pagina: chi
  // sta solo esplorando un prezzo non ha motivo di far girare quella query.
  useEffect(() => {
    if (!open || clienti !== null) return;
    api
      .get("/clienti")
      .then((r) => setClienti(r.data || []))
      .catch((e) => {
        setClienti([]);
        toast.error(messaggioErrore(e));
      });
  }, [open, clienti]);

  const submit = async () => {
    if (!calcolo || !form.cliente_id) return;
    setBusy(true);
    try {
      const r = await api.post("/preventivatore/da-calcolo", {
        cliente_id: form.cliente_id,
        totale_listino: calcolo.listino,
        totale_promotion: calcolo.promozione,
        livello: calcolo.livello,
        etichetta: form.etichetta.trim() || undefined,
        titolo: form.titolo.trim() || undefined,
        tipo_fornitura: form.tipo_fornitura,
      });
      toast.success("Preventivo creato con la prima configurazione dentro");
      nav(`/preventivi/preventivatore/${r.data.preventivo.id}`);
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  if (!calcolo) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="calc-crea-modal">
        <DialogHeader>
          <DialogTitle>Crea il preventivo da questo calcolo</DialogTitle>
          <DialogDescription>
            Nasce con i 20 blocchi in ordine e questo calcolo dentro, come prima
            configurazione del blocco prezzo.
          </DialogDescription>
        </DialogHeader>

        {/* Cosa sta per portarsi dietro, in chiaro: e' qui che si vede se si
            e' premuto il bottone sul calcolo sbagliato. */}
        <div
          className="rounded-md border bg-muted/40 px-4 py-group"
          data-testid="calc-crea-riepilogo"
        >
          <p className="text-eyebrow uppercase text-muted-foreground">
            I numeri che porta con sé
          </p>
          <div className="mt-inside grid grid-cols-2 gap-inside text-sm">
            <RigaRiepilogo etichetta="Totale listino" valore={eur(calcolo.listino)} />
            <RigaRiepilogo etichetta="Totale promotion" valore={eur(calcolo.promozione)} />
            {mostraLivello ? (
              <RigaRiepilogo etichetta="Livello" valore={calcolo.livello} />
            ) : null}
            <RigaRiepilogo
              etichetta="Prezzo"
              valore={eur(calcolo.prezzo)}
              testid="calc-crea-prezzo"
              forte
            />
          </div>
        </div>

        <div className="grid gap-card py-inside">
          <div className="grid gap-inside">
            <Label htmlFor="crea-cliente">Cliente</Label>
            <Select
              value={form.cliente_id}
              onValueChange={(v) => setForm({ ...form, cliente_id: v })}
            >
              <SelectTrigger id="crea-cliente" data-testid="calc-crea-cliente">
                <SelectValue
                  placeholder={clienti === null ? "Caricamento…" : "Seleziona…"}
                />
              </SelectTrigger>
              <SelectContent>
                {clienti && clienti.length ? (
                  clienti.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.ragione_sociale ||
                        `${c.nome || ""} ${c.cognome || ""}`.trim() ||
                        "—"}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    {clienti === null
                      ? "Caricamento…"
                      : "Nessun cliente disponibile: creane uno in Clienti."}
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-inside">
            <Label htmlFor="crea-tipo">Tipo di fornitura</Label>
            <Select
              value={form.tipo_fornitura}
              onValueChange={(v) => setForm({ ...form, tipo_fornitura: v })}
            >
              <SelectTrigger
                id="crea-tipo"
                aria-describedby="crea-tipo-aiuto"
                data-testid="calc-crea-tipo"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPI_FORNITURA.map((t) => (
                  <SelectItem key={t.valore} value={t.valore}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p id="crea-tipo-aiuto" className="text-xs text-muted-foreground">
              Decide quali passi di roadmap e quali omaggi nascono col preventivo.
            </p>
          </div>

          <div className="grid gap-inside">
            <Label htmlFor="crea-titolo">Titolo del preventivo (opzionale)</Label>
            <Input
              id="crea-titolo"
              value={form.titolo}
              onChange={(e) => setForm({ ...form, titolo: e.target.value })}
              placeholder="es. Cucina soggiorno"
              data-testid="calc-crea-titolo"
            />
          </div>

          <div className="grid gap-inside">
            <Label htmlFor="crea-etichetta">Nome della configurazione (opzionale)</Label>
            <Input
              id="crea-etichetta"
              value={form.etichetta}
              onChange={(e) => setForm({ ...form, etichetta: e.target.value })}
              placeholder="Configurazione 1"
              aria-describedby="crea-etichetta-aiuto"
              data-testid="calc-crea-etichetta"
            />
            <p id="crea-etichetta-aiuto" className="text-xs text-muted-foreground">
              È il nome che il cliente legge nella tabella dei prezzi.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !form.cliente_id}
            data-testid="calc-crea-submit"
          >
            {busy ? "Creazione…" : "Crea e apri il preventivo"}
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RigaRiepilogo({ etichetta, valore, testid, forte }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs text-muted-foreground">{etichetta}</p>
      <p
        className={[
          "tabular-nums",
          forte ? "font-semibold text-primary" : "font-medium text-foreground",
        ].join(" ")}
      >
        {valore}
      </p>
    </div>
  );
}

function ChainRow({ label, value, testid, muted, bold, emphasize }) {
  return (
    <div
      className={[
        "flex items-center justify-between px-4 py-2.5 text-sm",
        muted ? "text-muted-foreground" : "text-foreground",
      ].join(" ")}
      data-testid={testid}
    >
      <span>{label}</span>
      <span
        className={[
          "tabular-nums",
          bold ? "font-semibold text-foreground" : "",
          emphasize ? "font-semibold text-lg text-primary" : "",
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}
